/**
 * Does the brand mark read against the slide it landed on?
 *
 * The kit stores ONE logo file plus one boolean, `logoOnLight`, answered by looking at the file on a light
 * and a dark swatch. Placement is every slide or off, so a light mark inevitably meets a dark slide: the
 * generation prompt offers four slide backgrounds including near-black `#111115`, and licenses a dark
 * treatment on covers and dividers.
 *
 * The rule is the inversion of "never let a slide go dark": **on a slide the mark would not read against,
 * omit the mark.** The deck keeps its dark moments, the mark still lands on most slides, and nothing has to
 * be flattened.
 *
 * It is enforced in CODE at bake rather than asked of the model, because two soft prompt instructions
 * ("place the mark" + "judge the contrast") produce INDISTINGUISHABLE failures: a mark the model forgot and
 * a mark that vanished into a dark background look identical, to us and to the user.
 */

/** The `data-image-id` the generation prompt puts on the brand mark, and nothing else. */
export const BRAND_LOGO_IMAGE_ID = 'brand-logo'

/** One channel of sRGB, linearized for the WCAG relative-luminance formula. */
function channel(value: number): number {
  const c = value / 255
  return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4
}

/** WCAG relative luminance, 0 (black) to 1 (white). */
function luminance(r: number, g: number, b: number): number {
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b)
}

/** Every colour a CSS value mentions, as [r,g,b]. Handles #rgb, #rrggbb, rgb() and rgba(). */
function colorsIn(css: string): [number, number, number][] {
  const out: [number, number, number][] = []
  for (const m of css.matchAll(/#([0-9a-f]{3}|[0-9a-f]{6})\b/gi)) {
    const hex = m[1]
    const full = hex.length === 3 ? hex.split('').map((c) => c + c).join('') : hex
    out.push([parseInt(full.slice(0, 2), 16), parseInt(full.slice(2, 4), 16), parseInt(full.slice(4, 6), 16)])
  }
  for (const m of css.matchAll(/rgba?\(([^)]+)\)/gi)) {
    const parts = m[1].split(/[,/\s]+/).map((p) => p.trim()).filter(Boolean).map(Number)
    const [r, g, b] = parts
    const a = parts[3]
    // A fully transparent stop paints nothing, so it says nothing about what the mark sits on.
    if (a === 0) continue
    if ([r, g, b].every((n) => Number.isFinite(n))) out.push([r, g, b])
  }
  return out
}

/**
 * Is the slide's background light? `undefined` when it cannot be determined.
 *
 * `undefined` INPUT is not undeterminable: `slideBackground` returns undefined for a transparent root, and
 * the store then paints `#ffffff` (`generationActions.ts:237,351`). White is what actually renders, so that
 * is what the mark is judged against. Judging it against the kit's declared background would test it
 * against a colour the user never sees.
 *
 * A gradient is judged on its WORST stop rather than a dominant one: the mark has to read across the whole
 * sweep, so the stop closest to the mark's own tone is the one that decides. Only an unparseable value
 * returns undefined.
 */
export function backgroundIsLight(background: string | undefined | null, markIsDark: boolean): boolean | undefined {
  if (background == null || background.trim() === '') return true // transparent root → the store paints white
  const colors = colorsIn(background)
  if (colors.length === 0) return undefined
  const luminances = colors.map(([r, g, b]) => luminance(r, g, b))
  // Worst case FOR THE MARK: a dark mark is endangered by the darkest stop, a light mark by the lightest.
  const worst = markIsDark ? Math.min(...luminances) : Math.max(...luminances)
  return worst > 0.5
}

/**
 * Should the mark be kept on a slide with this background?
 *
 * **When the background cannot be determined, the mark is KEPT.** That direction is deliberate and it is
 * the whole point of enforcing this in code: a low-contrast logo is visible and someone can judge it, while
 * a dropped one is invisible and permanent, baked into the slide's HTML with nothing to notice. §4's real
 * enemy is failures you cannot tell apart, so the one failure mode left is the one you can see.
 */
export function markReadsOnBackground(background: string | undefined | null, logoOnLight: boolean): boolean {
  const isLight = backgroundIsLight(background, logoOnLight)
  if (isLight === undefined) return true
  // `logoOnLight` means the mark reads ON a light background, so it needs the background to be light.
  return isLight === logoOnLight
}
